import React from 'react';
import i18n from '@/i18n/config';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import type { CdnProviderForm } from '@/hooks/cdn-providers/useCdnProviderCrud';

interface CdnProviderFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CdnProviderForm;
  setFormData: React.Dispatch<React.SetStateAction<CdnProviderForm>>;
  isSubmitting: boolean;
  onSubmit: () => void;
  title: string;
  submitText: string;
}

export function CdnProviderFormModal({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  isSubmitting,
  onSubmit,
  title,
  submitText
}: CdnProviderFormModalProps) {
  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSubmitting && formData.name.trim() && formData.base_url.trim()) {
        onSubmit();
      }
    },
    [onSubmit, isSubmitting, formData.name, formData.base_url]
  );

  const handleClose = React.useCallback(() => {
    setFormData({ name: '', base_url: '' });
    onOpenChange(false);
  }, [setFormData, onOpenChange]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">{title}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Input
                  label={i18n.t('columns.name')}
                  placeholder={i18n.t('copy.enter_cdn_provider_name')}
                  value={formData.name}
                  onValueChange={(value) => setFormData((prev: CdnProviderForm) => ({ ...prev, name: value }))}
                  isRequired
                />
                <Input
                  label={i18n.t('copy.base_url')}
                  placeholder={i18n.t('copy.enter_base_url_e_g_https_cdn_example_com')}
                  value={formData.base_url}
                  onValueChange={(value) => setFormData((prev: CdnProviderForm) => ({ ...prev, base_url: value }))}
                  isRequired
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={onSubmit}
                isDisabled={!formData.name.trim() || !formData.base_url.trim()}
              >
                {submitText}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
